import {
	Frame,
	TopBar
} from '@shopify/polaris'
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react'
import { useSelector } from 'react-redux'
import {
	useLocation,
	useNavigate
} from 'react-router-dom'
import Logo from '../../img/logos/lappa-logo.svg'
import RoutesComponent from '../../routes/legacy/routesForTax'
import RoutesForBasicNew from '../../routes/routesForBasicNew'
import RoutesForEinvoice from '../../routes/legacy/routesForEinvoice'
import RoutesForEPR from '../../routes/legacy/routesForEPR'
import MainNavigationUnified from '../MainNavigation/MainNavigationUnified'
import ProjectNavigationWindow from '../ProjectNavigationWindow/ProjectNavigationWindow'
import RenderTopMenus from '../TopMenu/RenderTopMenus'
import { fetchApp } from '../../redux/features/user/userSlice'

import {
	getAppIdFromStorage,
} from '../../utils/appIdStorage'
import { isVerifyDomain } from '../../utils/environment'
import RoutesForAuthorize from '../../routes/legacy/routesForAuthorize'
import PublicVerifyRoutes from '../../routes/shared/PublicVerifyRoutes'

const AppComponentStartProject = ({
	loggingIn,
	loggingOut,
	user,
	i18n,
	isLogged,
	dispatch,
	defaultLanguage
}) => {
	const location = useLocation()
	const {
		openProjectName,
		app_Id,
		loadingApp
	} = useSelector(state => state.user)
	// Get app_id from localStorage instead of Redux
	const app_id = getAppIdFromStorage()

	// Redirect to login page when authentication is missing
	const navigate = useNavigate()
	const onVerifyDomain = isVerifyDomain()

	useEffect(() => {
		if (onVerifyDomain) {
			return
		}

			const isInvitationPage = location.pathname.startsWith('/invitation') || 
			location.pathname.startsWith('/invite/accept') ||
			location.pathname.startsWith('/invite/profile') ||
			location.pathname.startsWith('/join/accept') ||
			location.pathname.startsWith('/digital-compliance-label/')
		
		if (isInvitationPage) {
			return
		}
		
		const req = async () => {
			try {
				await dispatch(fetchApp())
			} catch (error) {
				console.error('Error fetching app data:', error)
				// Handle error appropriately
			}
		}
		req()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname])

	useEffect(() => {
		if (onVerifyDomain) {
			return
		}

		// Don't redirect during logout process
		if (loggingOut) {
			return
		}

		const hasAuthError = new URLSearchParams(location.search).has("auth_error");
		if (hasAuthError) {
			return;
		}

		const isSignupPublicPath =
			location.pathname === '/signup' || location.pathname.startsWith('/signup/')

		const allowedPublicPaths = [
			'/login',
			'/manager',
			'/forgot',
			'/forgot/:code',
			'/activation/:code',
			'/confirm-2fa',
			'/unsubscribe/:token',
			'/social/complete/google-oauth2/*',
			'/profile/sso_complete',
			'/return',
			'/join/accept',
			'/invitation',
			'/invitation/:token',
			'/invite/accept',
			'/invite/profile',
			'/digital-compliance-label/:labelToken',
		]

		// Check if current path is in the allowed list
		const isPublicPath =
			isSignupPublicPath ||
			allowedPublicPaths.some((path) => {
				if (path.includes(':')) {
					// For paths with parameters, check the beginning of the path
					return location.pathname.startsWith(path.split(':')[0])
				}
				return location.pathname === path
			})

		// Check if there's an API key in cookies/localStorage
		const getCookie = (name) => {
			const match = document.cookie.match(new RegExp('(^| )' + name + '=([^]+)'))
			return match ? decodeURIComponent(match[2]) : null
		}

		const hasApiKey = getCookie("api_key") || getCookie("API-KEY") || localStorage.getItem(
				"api_key") || localStorage.getItem("API-KEY")

		// If user is not logged in and not on a public path, redirect to login
		// But only if there's no API key (meaning no authentication attempt is in progress)
		if (!isLogged && !isPublicPath && !hasApiKey) {
			navigate(`/login`)
		}
	}, [isLogged, location, navigate, loggingOut, onVerifyDomain])


	const skipToContentRef = useRef(null)
	const [mobileNavigationActive, setMobileNavigationActive] = useState(false)
	const [userMenuActive, setUserMenuActive] = useState(false)
	const isUnsubscribePage = location.pathname.includes("/unsubscribe")
	const isInviteAcceptPage = location.pathname.includes("/invite/accept")
	const isInviteProfilePage = location.pathname.includes("/invite/profile")
	const isInvitePage = isInviteAcceptPage || isInviteProfilePage
	const isPublicDigitalLabelPage = location.pathname.startsWith('/digital-compliance-label/')

	useEffect(() => {
		if (defaultLanguage) {
			i18n.changeLanguage(defaultLanguage?.lang || "en")
		}
	}, [defaultLanguage, i18n])

	useEffect(() => {
		if (isLogged) {
			// fetchCurrentPlan is already called in the parent component
		}
	}, [isLogged])

	useEffect(() => {
		const frame = document.querySelector(".Polaris-Frame")
		if (frame) {
			frame.style.backgroundColor = (!isUnsubscribePage && !isInvitePage && isLogged) ? "inherit" : "white"
		}
	}, [isLogged, isUnsubscribePage, isInvitePage])


	const toggleUserMenuActive = useCallback(
			() => setUserMenuActive((userMenuActive) => !userMenuActive),
			[]
	)

	const toggleMobileNavigationActive = useCallback(
			() =>
					setMobileNavigationActive(
							(mobileNavigationActive) => !mobileNavigationActive
					),
			[]
	)

	const userMenuMarkup = (
			<TopBar.UserMenu
					actions={[{items: [{content: "Log out"}]}]}
					open={userMenuActive}
					onToggle={toggleUserMenuActive}
			/>
	)

	const topBarMarkup = (
			<TopBar
					showNavigationToggle
					userMenu={userMenuMarkup}
					onNavigationToggle={toggleMobileNavigationActive}
			/>
	)

	const projectComponents = useMemo(() => {
		let projectRoots = null
		let projectMenu = null

		// Use openProjectName from Redux (set by App.js based on app_id or hostname)
		// If openProjectName is not set, default to LTax
		const projectName = openProjectName || 'LTax'

		// Use unified navigation component for all projects
		projectMenu = <MainNavigationUnified user={user} />

		// Set project-specific routes
		// Use new route system if available, fallback to old system
		if (projectName === 'LInvoice') {
			projectRoots = <RoutesForEinvoice
					isLogged={isLogged}
					isLoggingIn={loggingIn}
			/>
		} else if (projectName === 'BASIC') {
			// Use new route system for BASIC
			projectRoots = <RoutesForBasicNew
					isLogged={isLogged}
					isLoggingIn={loggingIn}
			/>
		} else if (projectName === 'LEPR') {
			projectRoots = <RoutesForEPR
					isLogged={isLogged}
					isLoggingIn={loggingIn}
			/>
		} else if (projectName === 'LAuthorize') {
			projectRoots = <RoutesForAuthorize
					isLogged={isLogged}
					isLoggingIn={loggingIn}
			/>
		} else {
			// Default to LTax (RoutesComponent)
			projectRoots = <RoutesComponent
					isLogged={isLogged}
					isLoggingIn={loggingIn}
			/>
		}

		return {
			projectRoots,
			projectMenu
		}
	}, [openProjectName, isLogged, loggingIn, user])

	if (onVerifyDomain) {
		return <PublicVerifyRoutes />
	}

	// When app_id is 0, show only the project selector modal (no Frame) so user doesn't see Tax first
	const showProjectSelectorOnly = loadingApp === false && isLogged && app_Id === 0 && !user?.is_manager

	// Don't render Frame for standalone/public pages
	if (isUnsubscribePage || isInvitePage || isPublicDigitalLabelPage) {
		return <>{projectComponents.projectRoots}</>
	}

	return (
			<>
				{showProjectSelectorOnly && <ProjectNavigationWindow />}
				{/* Don't render Frame when showing project selector — only gray background + modal */}
				{!showProjectSelectorOnly && (
					<Frame
						logo={{
							width: 100,
							topBarSource: Logo,
							contextualSaveBarSource: Logo,
							url: "/",
							accessibilityLabel: "Lappa",
						}}
						showMobileNavigation={mobileNavigationActive}
						onNavigationDismiss={toggleMobileNavigationActive}
						skipToContentTarget={skipToContentRef.current}
						topBar={isLogged ? topBarMarkup : null}
						navigation={
								!isUnsubscribePage && !isInvitePage && isLogged && projectComponents.projectMenu
						}
					>
						{!isUnsubscribePage && !isInvitePage && isLogged && <RenderTopMenus />}
						{projectComponents.projectRoots}
					</Frame>
				)}
			</>
	)
}

export default AppComponentStartProject